// src/client/renderer/vfx/tile/void-fall-pool.ts
//
// Plan 16, Task 4.2 — Void fall pool.
//
// Triggered by LandingEvent when an injected isVoidNode(nodeId) callback
// returns true. Plan 19 supplies the real void-node semantic; for Plan 16,
// the resolver returns null when the callback is absent or returns false.
// No tile type in src/shared/board/types.ts is currently named "void" —
// the void-node concept is map-level, not type-level.
//
// Geometry choice: THREE.PlaneGeometry(0.5, 1.5), oriented vertically (no
// rotation applied — PlaneGeometry lies in the XY plane by default, which
// gives a vertical rectangular column when viewed from camera level). This
// produces a downward smoke-trail silhouette: a narrow vertical column that
// slides down as the player falls into the void. PlaneGeometry was chosen
// over CylinderGeometry for lower triangle count (2 vs 16 tris per emitter)
// and simpler UV layout for future texture work.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.800s  position.y: start → start - 1.5  (translate downward 1.5 units)
//   0.000–0.800s  opacity: 1 → 0                    (fade out via opacityProxy)
// Total lifetime: 800ms. One-shot — isFinished set via tl.call(fn, undefined, 0.8)
// (NOT eventCallback — see gsap-registry.ts VIRTUAL_DURATION note).
//
// Colour: 0x222244 (dark blue-grey — void/abyss palette).
//
// Max-active: 2 (void tiles are rare; 2 simultaneous void landings is generous).
//
// Position note: The resolver supplies {x:0, y:0, z:0} placeholder — the
// wire-up site (Task 5.1) resolves nodeId→world-pos and passes the real
// position via manager.spawn(). Same pattern as warp-swirl-pool (Task 4.1).
//
// Forward-defense F4: pool.dispose() iterates active emitters, kills timelines,
//   frees per-emitter materials, then disposes shared geometry once. Idempotent.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const LIFETIME = 0.8; // seconds (800ms)
const FALL_DISTANCE = 1.5; // units downward

/** Void abyss dark blue-grey. */
const VOID_COLOR = 0x222244;

/** Max concurrent active emitters. */
const MAX_ACTIVE = 2;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (PlaneGeometry — vertical smoke-trail column)
// ─────────────────────────────────────────────────────────────────────────────

const PLANE_WIDTH = 0.5;
const PLANE_HEIGHT = 1.5;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface VoidFallPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createVoidFallPool({ scene, registry }: VoidFallPoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.PlaneGeometry(PLANE_WIDTH, PLANE_HEIGHT);

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Per-emitter material clone (additive blend optional — use transparent only
    // to keep the dark void colour readable without washing out)
    const material = new THREE.MeshBasicMaterial({
      color: VOID_COLOR,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('void_fall');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween 1: position.y falls downward 1.5 units over 800ms via proxy
    const posProxy = { value: args.position.y };
    const targetY = args.position.y - FALL_DISTANCE;
    tl.to(
      posProxy,
      {
        value: targetY,
        duration: LIFETIME,
        ease: 'power1.in',
        onUpdate: () => {
          mesh.position.y = posProxy.value;
        },
      },
      0,
    );

    // Tween 2: opacity 1 → 0 over 800ms via proxy
    const opacityProxy = { value: 1.0 };
    tl.to(
      opacityProxy,
      {
        value: 0.0,
        duration: LIFETIME,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method returns the
  // current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees ALL GPU resources
  // WITHOUT expecting prior release() calls. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `LandingEvent` on a void node to void fall spawn args.
 *
 * Returns null for:
 *   - any non-landing event
 *   - landing events when isVoidNode callback is not provided
 *   - landing events when isVoidNode(nodeId) returns false
 *
 * Position note: LandingEvent (src/shared/game/types.ts:363-368) carries
 * nodeId but NO world-position. The position placeholder {x:0, y:0, z:0}
 * is intentional — the wire-up site (Task 5.1) resolves nodeId→world-pos
 * and passes the real position via manager.spawn(). This follows the same
 * pattern as resolveWarpSwirlForEvent (Task 4.1).
 *
 * The isVoidNode callback is supplied by Plan 19. For Plan 16,
 * this resolver returns null whenever the callback is absent, letting the
 * pool participate in the standard event bus without hard-coupling to the
 * board graph.
 */
export function resolveVoidFallForEvent(
  event: GameEvent,
  isVoidNode?: (nodeId: string) => boolean,
): { poolId: 'void_fall'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'landing') return null;
  if (isVoidNode === undefined) return null;
  if (isVoidNode(event.nodeId) !== true) return null;

  return {
    poolId: 'void_fall',
    args: {
      position: { x: 0, y: 0, z: 0 }, // wire-up (Task 5.1) supplies real position
    },
  };
}
